/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { CrudListType, CrudTemplate, ListColumns } from '../../../organisms/crud/form-types';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import { PharmacyOrder } from 'app/types/pharmacy-order';
import { identity, pickBy } from 'lodash';
import { resolveAppPath } from 'app/core/utils/url-resolver';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<PharmacyOrder> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    size: '100px',
    render: item => <IdCopy>{String(item.id)}</IdCopy>
  },
  {
    title: translate('common.table.columns.externalOrderNumber'),
    dataIndex: 'externalOrderNumber',
    key: 'externalOrderNumber',
    size: '150px',
    render: item => item.externalOrderNumber || '-'
  },
  {
    title: translate('common.table.columns.pharmacy'),
    dataIndex: 'pharmacy',
    key: 'pharmacy',
    size: '200px',
    render: item => item.pharmacy?.name || '-'
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name',
    size: '200px',
    render: item => item.user?.name || '-'
  },
  {
    title: translate('common.table.columns.phoneNumber'),
    dataIndex: 'phoneNumber',
    key: 'phoneNumber',
    size: '150px',
    render: item => item.user?.phoneNumber || '-'
  },
  {
    title: translate('common.table.columns.status'),
    dataIndex: 'status',
    key: 'status',
    size: '100px',
    render: item => item.status
  },
  {
    title: translate('common.table.columns.deliveryMethod'),
    dataIndex: 'deliveryMethod',
    key: 'deliveryMethod',
    size: '120px',
    render: item => {
      const method = item.deliveryMethod;
      return method === 'pickUp'
        ? translate('common.labels.pickUp')
        : method === 'homeDelivery'
        ? translate('common.labels.homeDelivery')
        : '-';
    }
  },
  {
    title: translate('common.table.columns.prescriptions'),
    dataIndex: 'prescriptions',
    key: 'prescriptions',
    size: '120px',
    render: item => {
      const hasPrescriptions = item.prescriptions?.length ?? 0 > 0;
      return hasPrescriptions ? `✓ (${item.prescriptions?.length ?? 0})` : '-';
    }
  },
  {
    title: translate('common.table.columns.reason'),
    dataIndex: 'reason',
    key: 'reason',
    size: '1fr',
    render: item => item.reason || '-'
  },
  {
    title: translate('common.table.columns.submittedOn'),
    dataIndex: 'submittedOn',
    key: 'submittedOn',
    size: '180px',
    render: item => (item.submittedOn ? <DateTime date={item.submittedOn} /> : '-')
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '180px',
    render: item => <DateTime date={item.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    size: '180px',
    render: item => <DateTime date={item.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'status',
    inline: true,
    label: translate('common.labels.status'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.labels.created'),
        value: 'created'
      },
      {
        label: translate('common.labels.submitted'),
        value: 'submitted'
      },
      {
        label: translate('common.labels.failed'),
        value: 'failed'
      },
      {
        label: translate('common.labels.pending'),
        value: 'pending'
      },
      {
        label: translate('common.labels.processed'),
        value: 'processed'
      },
      {
        label: translate('common.labels.delivered'),
        value: 'delivered'
      },
      {
        label: translate('common.labels.canceled'),
        value: 'canceled'
      }
    ]
  },
  {
    type: 'selectSearch',
    name: 'deliveryMethod',
    inline: true,
    label: translate('common.labels.deliveryMethod'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.labels.pickUp'),
        value: 'pickUp'
      },
      {
        label: translate('common.labels.homeDelivery'),
        value: 'homeDelivery'
      }
    ]
  }
];

/**
 * List.
 */

const list = (translate: TFunction): CrudListType<PharmacyOrder> => ({
  columns: listColumns(translate),
  filters: listFilters(translate),
  enableExport: true,
  resolveParams: (params: any, item: any) => {
    console.log({
      ...params,
      ...item
    });

    return {
      ...params,
      ...item
    };
  },
  normalizeFilters: (data: any) => {
    const { ...filters } = data.filters;
    const result: any = {
      filters: pickBy(filters, identity),
      order: [
        {
          key: 'createdAt',
          direction: 'DESC'
        }
      ]
    };

    return result;
  },
  clickLine: (item, { navigate }) => {
    const route = resolveAppPath(appRoutes.dashboard.services.orderDetail, {
      id: item.id
    });

    navigate(route, { state: { order: item } });
  },
  route: appRoutes.dashboard.services.orders,
  addButton: translate('common.actions.add'),
  canEdit: false,
  canRemove: true,
  key: ['ccis'],
  pagination: {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100, 500, 1000]
  },
  endpoint: apiEndpoints.orders
});

/**
 * Config.
 */

export function createOrdersTemplate(translate: TFunction): CrudTemplate<PharmacyOrder> {
  return normalizeTemplate(translate, {
    list: list(translate)
  });
}
